import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Logo from './Logo';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_URL } from '../lib/apiBase';
import {
  Plus,
  Rss,
  Sparkles,
  LayoutGrid,
  Rocket,
  AlertTriangle,
  XCircle,
  Zap,
  Map,
  Megaphone,
  Mail,
  Share2,
  Settings,
  BookOpen,
  HelpCircle,
  ChevronsUpDown,
  ReceiptText,
  Sun,
  Moon,
  Command,
} from 'lucide-react';

const navItemBase =
  'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';

const NavItem = ({ icon: Icon, label, count, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`${navItemBase} w-full ${
      active
        ? 'bg-primary/10 text-primary font-medium'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`}
  >
    <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
    <span className="truncate flex-1 text-left">{label}</span>
    {typeof count === 'number' && (
      <Badge
        variant={active ? 'default' : 'secondary'}
        className="h-5 px-1.5 text-[10px] tabular-nums"
      >
        {count}
      </Badge>
    )}
  </button>
);

const SectionLabel = ({ children }) => (
  <div className="px-2.5 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </div>
);

/**
 * AppSidebar — persistent left navigation for the Dashboard and its sub-routes.
 * Renders lane counts live from /api/stats. Handles workspace switcher entry point.
 */
export const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace, currentWorkspaceId, usage } = useWorkspace();
  const { isDark, toggleTheme } = useTheme();
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    if (!currentWorkspaceId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/stats`, {
        params: { workspace_id: currentWorkspaceId },
      });
      setStats(data);
    } catch (_e) {
      // best-effort — sidebar counts are decorative, not blocking
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    loadStats();
    // Refresh counts every 30s while sidebar is mounted
    const iv = setInterval(loadStats, 30000);
    return () => clearInterval(iv);
  }, [loadStats]);

  const lanes = stats?.lanes || {};
  const total =
    (lanes.build_next || 0) + (lanes.fix_now || 0) + (lanes.ignore || 0);
  const quickWins = stats?.quick_wins ?? 0;

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const goLane = (lane) => {
    if (lane === 'all') {
      navigate('/app');
    } else {
      navigate(`/app?lane=${lane}`);
    }
  };

  return (
    <aside
      className="hidden lg:flex lg:flex-col lg:w-[240px] lg:flex-shrink-0 h-screen sticky top-0 border-r bg-card"
      data-testid="app-sidebar"
    >
      {/* Workspace header */}
      <button
        type="button"
        onClick={() => navigate('/app')}
        className="flex items-center justify-between gap-2 p-3 border-b hover:bg-muted/50 transition-colors text-left"
        data-testid="app-sidebar-workspace-header"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">{currentWorkspace?.icon || '📦'}</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {currentWorkspace?.name || 'Workspace'}
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {(usage?.plan || 'Demo')} plan
            </div>
          </div>
        </div>
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      </button>

      {/* Nav sections */}
      <ScrollArea className="flex-1">
        <div className="px-2 py-2 space-y-0.5">
          {/* COLLECT */}
          <SectionLabel>Collect</SectionLabel>
          <NavItem
            icon={Plus}
            label="Add signals"
            active={isActive('/import', true)}
            onClick={() => navigate('/import')}
            testId="sidebar-add-signals"
          />
          <NavItem
            icon={Rss}
            label="Signal Sources"
            active={isActive('/sources')}
            onClick={() => navigate('/sources')}
            testId="sidebar-signal-sources"
          />

          {/* TRIAGE */}
          <SectionLabel>Triage</SectionLabel>
          <NavItem
            icon={LayoutGrid}
            label="All decisions"
            count={stats ? total : undefined}
            active={
              isActive('/app', true) &&
              !new URLSearchParams(location.search).get('lane')
            }
            onClick={() => goLane('all')}
            testId="sidebar-lane-all"
          />
          <NavItem
            icon={Rocket}
            label="Build Next"
            count={lanes.build_next}
            active={new URLSearchParams(location.search).get('lane') === 'build_next'}
            onClick={() => goLane('build_next')}
            testId="sidebar-lane-build"
          />
          <NavItem
            icon={AlertTriangle}
            label="Fix Now"
            count={lanes.fix_now}
            active={new URLSearchParams(location.search).get('lane') === 'fix_now'}
            onClick={() => goLane('fix_now')}
            testId="sidebar-lane-fix"
          />
          <NavItem
            icon={XCircle}
            label="Ignore"
            count={lanes.ignore}
            active={new URLSearchParams(location.search).get('lane') === 'ignore'}
            onClick={() => goLane('ignore')}
            testId="sidebar-lane-ignore"
          />
          <NavItem
            icon={Zap}
            label="Quick Wins"
            count={quickWins}
            active={new URLSearchParams(location.search).get('filter') === 'quick_wins'}
            onClick={() => navigate('/app?filter=quick_wins')}
            testId="sidebar-quick-wins"
          />

          {/* SHIP */}
          <SectionLabel>Ship</SectionLabel>
          <NavItem
            icon={Map}
            label="Roadmap"
            active={isActive('/roadmap', true)}
            onClick={() => navigate('/roadmap')}
            testId="sidebar-roadmap"
          />
          <NavItem
            icon={ReceiptText}
            label="Decision Receipts"
            active={isActive('/receipts', true)}
            onClick={() => navigate('/receipts')}
            testId="sidebar-receipts"
          />
          <NavItem
            icon={Megaphone}
            label="Changelog"
            active={isActive('/changelog', true)}
            onClick={() => navigate('/changelog')}
            testId="sidebar-changelog"
          />
          <NavItem
            icon={Mail}
            label="Weekly Digest"
            active={isActive('/digest', true)}
            onClick={() => navigate('/digest')}
            testId="sidebar-digest"
          />
          <NavItem
            icon={Share2}
            label="Share & Public URLs"
            active={isActive('/share', true)}
            onClick={() => navigate('/share')}
            testId="sidebar-share"
          />

          {/* WORKSPACE */}
          <SectionLabel>Workspace</SectionLabel>
          <NavItem
            icon={Settings}
            label="Settings"
            active={isActive('/settings')}
            onClick={() => navigate('/settings/branding')}
            testId="sidebar-settings"
          />
          <NavItem
            icon={BookOpen}
            label="Playbook"
            active={isActive('/playbook', true)}
            onClick={() => navigate('/playbook')}
            testId="sidebar-playbook"
          />
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-2 space-y-0.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleTheme}
            className={`${navItemBase} flex-1 text-muted-foreground hover:bg-muted hover:text-foreground`}
            data-testid="sidebar-theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <Sun className="h-4 w-4 flex-shrink-0" />
            ) : (
              <Moon className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate flex-1 text-left">
              {isDark ? 'Light mode' : 'Dark mode'}
            </span>
          </button>
          <span
            className="flex items-center gap-0.5 rounded border bg-muted/60 px-1.5 py-1 text-[10px] font-medium text-muted-foreground"
            title="Press Cmd/Ctrl + K to open the command palette"
            data-testid="sidebar-cmdk-hint"
          >
            <Command className="h-3 w-3" />K
          </span>
        </div>
        <NavItem
          icon={HelpCircle}
          label="Help & FAQ"
          active={isActive('/faq', true)}
          onClick={() => navigate('/faq')}
          testId="sidebar-help"
        />
        <Separator className="my-1" />
        <button
          type="button"
          onClick={() => navigate('/settings/branding')}
          className="flex items-center gap-2 w-full rounded-md p-2 hover:bg-muted transition-colors"
          data-testid="sidebar-user-chip"
        >
          <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
            D
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium truncate">Demo user</div>
            <div className="text-[10px] text-muted-foreground">Alpha access</div>
          </div>
        </button>
      </div>
    </aside>
  );
};

export default AppSidebar;
